import type { TeamPermissions } from '@rhythmic/db/types'
import type { WorkspaceMemberUser } from '@rhythmic/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import { useLiveQuery } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'
import { WORKSPACE_MEMBER_USER_TABLE } from '../collections'

import { useCollection } from '../composables/useCollection'

export interface WorkspaceUser {
  id: string
  workspace_id: string
  full_name: string | null
  email: string | null
  avatar_url: string | null
  role: TeamPermissions | null
  admin: boolean | null
  intercom_user: string | null
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  updated_at: Date
}
export function useWorkspaceUsers(workspace_id: string): UseLiveQueryReturn<WorkspaceUser> | undefined {
  const logger = useRhythmicLogger('useWorkspaceUsers')
  const users = useCollection<WorkspaceMemberUser>(
    WORKSPACE_MEMBER_USER_TABLE,
    { workspaceId: workspace_id },
    'query',
  ).collection

  if (!users) {
    logger.warn('Collections not available yet')
    return undefined
  }

  const query = useLiveQuery((builder) =>
    builder.from({ u: users }).select(({ u }) => ({
      id: u.id,
      workspace_id: u.workspace_id,
      full_name: u.full_name || u.email,
      email: u.email,
      avatar_url: u.avatar_url,
      role: u.role as TeamPermissions | null,
      admin: u.admin ?? false,
      intercom_user: u.intercom_user,
      stripe_customer_id: u.stripe_customer_id,
      stripe_subscription_id: u.stripe_subscription_id,
      updated_at: u.updated_at,
    })),
  ) as UseLiveQueryReturn<WorkspaceUser>

  logger.debug(`Loaded workspace users for workspace ${workspace_id}`, {
    query,
  })

  return query
}
